import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Copy, Share2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSend, type SendOptions } from './useSend';

interface SendSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefillText?: string;
  prefillName?: string;
}

const DELETION_OPTIONS = [
  { value: '1h', label: '1 час' },
  { value: '1d', label: '1 день' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
];

const EXPIRY_OPTIONS = [
  { value: '1h', label: '1 час' },
  { value: '24h', label: '24 часа' },
  { value: '72h', label: '72 часа' },
  { value: '7d', label: '7 дней' },
  { value: 'never', label: 'Никогда' },
];

const MAX_ACCESS_OPTIONS = [
  { value: '1', label: '1 раз' },
  { value: '5', label: '5 раз' },
  { value: '10', label: '10 раз' },
  { value: 'unlimited', label: 'Без лимита' },
];

function addDuration(hours: number): string {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function parseDuration(value: string): string | null {
  switch (value) {
    case '1h': return addDuration(1);
    case '24h': return addDuration(24);
    case '72h': return addDuration(72);
    case '1d': return addDuration(24);
    case '7d': return addDuration(7 * 24);
    case '30d': return addDuration(30 * 24);
    case 'never': return null;
    default: return null;
  }
}

export function SendSheet({ open, onOpenChange, prefillText, prefillName }: SendSheetProps) {
  const { createSend } = useSend();

  const [text, setText] = useState(prefillText || '');
  const [name, setName] = useState(prefillName || 'Отправка');
  const [deletion, setDeletion] = useState('7d');
  const [expiry, setExpiry] = useState('72h');
  const [maxAccess, setMaxAccess] = useState('1');
  const [hidden, setHidden] = useState(true);

  const [isCreating, setIsCreating] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Full reset when sheet opens (fixes stale result bug)
  useEffect(() => {
    if (open) {
      setText(prefillText || '');
      setName(prefillName || 'Отправка');
      setDeletion('7d');
      setExpiry('72h');
      setMaxAccess('1');
      setHidden(true);
      setResultUrl(null);
      setCopied(false);
      setIsCreating(false);
    }
  }, [open, prefillText, prefillName]);

  const handleCreate = async () => {
    if (!text.trim()) {
      toast.error('Введите текст');
      return;
    }
    if (text.length > 1000) {
      toast.error('Максимум 1000 символов');
      return;
    }

    setIsCreating(true);
    const options: SendOptions = {
      deletionDate: parseDuration(deletion)!,
      expirationDate: parseDuration(expiry),
      maxAccessCount: maxAccess === 'unlimited' ? null : parseInt(maxAccess),
      hidden,
    };

    const url = await createSend(text, name, options);
    setIsCreating(false);

    if (url) {
      setResultUrl(url);
    }
  };

  const handleCopy = async () => {
    if (!resultUrl) return;
    try {
      await navigator.clipboard.writeText(resultUrl);
      setCopied(true);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API может быть недоступен
      toast.info('Скопируйте ссылку вручную');
    }
  };

  const handleShare = async () => {
    if (!resultUrl || !navigator.share) return;
    try {
      await navigator.share({ title: name, url: resultUrl });
    } catch {
      // User cancelled or share failed
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Bitwarden Send
          </SheetTitle>
          <SheetDescription>
            Безопасная передача текста по одноразовой ссылке
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-auto space-y-4 py-4">
          {!resultUrl ? (
            <>
              {/* Текст */}
              <div className="space-y-1.5">
                <Label>Текст</Label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="Текст или пароль для отправки..."
                />
                <div className="text-xs text-muted-foreground text-right">{text.length}/1000</div>
              </div>

              {/* Имя */}
              <div className="space-y-1.5">
                <Label>Название</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              {/* Опции */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Удаление</Label>
                  <Select value={deletion} onValueChange={setDeletion}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DELETION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Истечение</Label>
                  <Select value={expiry} onValueChange={setExpiry}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EXPIRY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Макс. просмотров</Label>
                  <Select value={maxAccess} onValueChange={setMaxAccess}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MAX_ACCESS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={hidden}
                      onChange={(e) => setHidden(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    Скрыть текст <span className="text-xs text-muted-foreground font-normal">(получатель увидит после клика)</span>
                  </label>
                </div>
              </div>

              {/* Создать */}
              <Button onClick={handleCreate} disabled={isCreating || !text.trim()} className="w-full">
                {isCreating ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Создание...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Создать Send</>
                )}
              </Button>
            </>
          ) : (
            /* Результат — ссылка */
            <div className="space-y-4">
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="text-sm font-medium text-green-800 mb-2">Send создан!</div>
                <div className="text-xs text-green-700 break-all font-mono bg-white rounded p-2 border">
                  {resultUrl}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleCopy} variant="outline" className="flex-1">
                  {copied ? (
                    <><Check className="w-4 h-4 mr-2" /> Скопировано</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Копировать</>
                  )}
                </Button>
                {typeof navigator !== 'undefined' && 'share' in navigator && (
                  <Button onClick={handleShare} variant="outline" className="flex-1">
                    <Share2 className="w-4 h-4 mr-2" /> Поделиться
                  </Button>
                )}
              </div>

              <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">
                Закрыть
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
